<script>
  import { onMount } from 'svelte';
  import { pwaInstallPrompt } from '../../stores.js';

  let dismissed = false;
  let isInstalled = false;
  let installing = false;
  let showSuccess = false;

  // OS / browser detection
  let os = 'unknown';      // 'ios' | 'android' | 'desktop'
  let browser = 'unknown'; // 'safari' | 'chrome' | 'firefox' | 'samsung' | 'edge' | 'other'

  $: deferredPrompt = $pwaInstallPrompt;
  $: isInstallable = !!deferredPrompt;

  onMount(() => {
    dismissed = sessionStorage.getItem('pwaInstallBannerDismissed') === 'true';

    if (window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true) {
      isInstalled = true;
      return;
    }

    const ua = window.navigator.userAgent;

    // OS detection
    if (/iphone|ipad|ipod/i.test(ua) && !window.MSStream) {
      os = 'ios';
    } else if (/android/i.test(ua)) {
      os = 'android';
    } else {
      os = 'desktop';
    }

    // Browser detection
    if (/samsungbrowser/i.test(ua)) {
      browser = 'samsung';
    } else if (/firefox|fxios/i.test(ua)) {
      browser = 'firefox';
    } else if (/edg\//i.test(ua)) {
      browser = 'edge';
    } else if (/chrome|crios|chromium/i.test(ua)) {
      browser = 'chrome';
    } else if (/safari/i.test(ua)) {
      browser = 'safari';
    } else {
      browser = 'other';
    }

    const handleAppInstalled = () => {
      isInstalled = true;
      pwaInstallPrompt.set(null);
      showSuccess = true;
      setTimeout(() => dismiss(), 3000);
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  });

  function dismiss() {
    dismissed = true;
    sessionStorage.setItem('pwaInstallBannerDismissed', 'true');
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    installing = true;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome !== 'accepted') installing = false;
    pwaInstallPrompt.set(null);
  }

  $: shouldShow = !dismissed && !isInstalled;
</script>

{#if shouldShow}
  <div class="banner" class:success={showSuccess}>
    <div class="banner-content">
      {#if showSuccess}
        <div class="success-message">
          <svg class="icon-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <span>Application installée avec succès !</span>
        </div>

      {:else if isInstallable}
        <!-- Chrome/Edge desktop ou Android Chrome : prompt natif disponible -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer l'application</h3>
            <p>Accédez rapidement à vos quiz depuis votre écran d'accueil</p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-enable" disabled={installing} on:click={handleInstall}>
            {#if installing}
              <span class="spinner"></span>Installation…
            {:else}
              Installer
            {/if}
          </button>
          <button class="btn-dismiss" on:click={dismiss}>Plus tard</button>
        </div>

      {:else if os === 'ios' && browser === 'safari'}
        <!-- iOS Safari : instructions natives -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer sur votre iPhone</h3>
            <p>
              Appuyez sur
              <span class="step">
                <svg class="step-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                </svg>
                Partager
              </span>
              puis
              <span class="step">
                <svg class="step-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                Sur l'écran d'accueil
              </span>
            </p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-dismiss" on:click={dismiss}>Fermer</button>
        </div>

      {:else if os === 'ios'}
        <!-- iOS Chrome/Firefox : rediriger vers Safari -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer sur votre iPhone</h3>
            <p>
              Ouvrez cette page dans
              <span class="step">Safari</span>
              puis appuyez sur
              <span class="step">
                <svg class="step-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                </svg>
                Partager
              </span>
              →
              <span class="step">
                <svg class="step-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                </svg>
                Sur l'écran d'accueil
              </span>
            </p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-dismiss" on:click={dismiss}>Fermer</button>
        </div>

      {:else if os === 'android' && browser === 'samsung'}
        <!-- Samsung Browser -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer sur votre Samsung</h3>
            <p>
              Appuyez sur
              <span class="step">⋮ Menu</span>
              →
              <span class="step">Ajouter la page à</span>
              →
              <span class="step">Écran d'accueil</span>
            </p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-dismiss" on:click={dismiss}>Fermer</button>
        </div>

      {:else if os === 'android' && browser === 'firefox'}
        <!-- Android Firefox -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer sur Android</h3>
            <p>
              Appuyez sur
              <span class="step">⋮ Menu</span>
              →
              <span class="step">Installer</span>
            </p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-dismiss" on:click={dismiss}>Fermer</button>
        </div>

      {:else if os === 'android'}
        <!-- Android autre navigateur : recommander Chrome -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer sur Android</h3>
            <p>
              Ouvrez cette page dans
              <span class="step">Chrome</span>
              puis appuyez sur
              <span class="step">⋮ Menu</span>
              →
              <span class="step">Ajouter à l'écran d'accueil</span>
            </p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-dismiss" on:click={dismiss}>Fermer</button>
        </div>

      {:else if os === 'desktop' && (browser === 'chrome' || browser === 'edge')}
        <!-- Desktop Chrome / Edge -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer l'application</h3>
            <p>
              Cliquez sur l'icône
              <span class="step">
                <svg class="step-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                Installer
              </span>
              dans la barre d'adresse de {browser === 'edge' ? 'Edge' : 'Chrome'}
            </p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-dismiss" on:click={dismiss}>Fermer</button>
        </div>

      {:else}
        <!-- Desktop Firefox / Safari / autre : recommander Chrome ou Edge -->
        <div class="banner-left">
          <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
          <div class="banner-text">
            <h3>Installer l'application</h3>
            <p>
              Pour installer, ouvrez cette page dans
              <span class="step">Chrome</span>
              ou
              <span class="step">Edge</span>
              et cliquez sur l'icône d'installation dans la barre d'adresse
            </p>
          </div>
        </div>
        <div class="banner-actions">
          <button class="btn-dismiss" on:click={dismiss}>Fermer</button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .banner {
    background: linear-gradient(135deg, var(--primary-gradient-start) 0%, var(--primary-gradient-end) 100%);
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
    animation: slideDown 0.4s ease-out;
  }

  .banner.success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
  }

  .icon,
  .icon-success {
    width: 2rem;
    height: 2rem;
    color: white;
    flex-shrink: 0;
  }

  .banner-text h3 {
    margin: 0;
    color: white;
    font-size: 1rem;
    font-weight: 600;
  }

  .banner-text p {
    margin: 0.25rem 0 0 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    line-height: 1.6;
  }

  .success-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: white;
    font-weight: 500;
  }

  .step {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 4px;
    padding: 0.1rem 0.4rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .step-icon {
    width: 0.9rem;
    height: 0.9rem;
    flex-shrink: 0;
  }

  .banner-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  .btn-enable,
  .btn-dismiss {
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }

  .btn-enable {
    background: white;
    color: #d97706;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-enable:hover:not(:disabled) {
    background: #f9fafb;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .btn-enable:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .btn-dismiss {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  .btn-dismiss:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  .spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid rgba(217, 119, 6, 0.3);
    border-top-color: #d97706;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    .banner { padding: 1rem; }

    .banner-content {
      flex-direction: column;
      align-items: stretch;
    }

    .banner-left { gap: 0.75rem; }

    .icon,
    .icon-success {
      width: 1.5rem;
      height: 1.5rem;
    }

    .banner-text h3 { font-size: 0.9rem; }
    .banner-text p  { font-size: 0.8rem; }

    .banner-actions { width: 100%; }

    .btn-enable,
    .btn-dismiss {
      flex: 1;
      justify-content: center;
    }
  }
</style>
